import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, Loader2, ShieldX } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { STAGES } from "@/lib/constants";
import { useCrmMeta } from "@/hooks/useCrmMeta";
import { useRealtime } from "@/hooks/useRealtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChannelBadge } from "@/components/crm/Badges";
import { PersonalDataPanel } from "@/components/crm/lead/PersonalDataPanel";
import { CommercialPanel } from "@/components/crm/lead/CommercialPanel";
import { FollowUpPanel } from "@/components/crm/lead/FollowUpPanel";
import { ConversationsPanel } from "@/components/crm/lead/ConversationsPanel";
import { ActivityPanel } from "@/components/crm/lead/ActivityPanel";
import { SCHEDULED, useStageGuard } from "@/hooks/useStageGuard";
import { AppointmentsPanel } from "@/components/crm/lead/AppointmentsPanel";

export default function LeadDetailPage() {
  const { id } = useParams();
  const { services, sellers } = useCrmMeta();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    api.get(`/leads/${id}`).then((r) => setData(r.data)).catch((e) => e.response?.status === 404 && setNotFound(true));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useRealtime((ev) => { if (ev.lead_id === id) load(); });
  const { guard, dialog } = useStageGuard();

  const update = async (patch) => {
    try {
      await api.patch(`/leads/${id}`, patch);
      toast.success("Cambios guardados");
      load();
      return true;
    } catch (err) {
      toast.error(errorMessage(err));
      return false;
    }
  };

  if (notFound) {
    return (
      <div className="flex flex-col items-center py-24 text-center" data-testid="lead-not-found">
        <ShieldX className="h-10 w-10 text-slate-300" />
        <h1 className="mt-4 font-display text-xl font-bold">Lead no encontrado</h1>
        <Link to="/crm/leads" className="mt-4 text-sm font-medium text-[#1E6FD9]">Volver a leads</Link>
      </div>
    );
  }
  if (!data) return <div className="grid py-24 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#1E6FD9]" /></div>;

  const { lead } = data;
  return (
    <div data-testid="lead-detail-page">
      <Link to="/crm/leads" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1E6FD9]" data-testid="lead-back-link">
        <ArrowLeft className="h-4 w-4" /> Leads
      </Link>
      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="lead-detail-name">{lead.full_name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ChannelBadge channel={lead.channel} testid="lead-detail-channel" />
            {lead.has_appointment_request && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700" data-testid="lead-appointment-badge">
                <CalendarClock className="h-3.5 w-3.5" /> Solicitud de cita · {lead.appointment_preferred_date} {lead.appointment_preferred_time}
              </span>
            )}
          </div>
        </div>
        <Select value={lead.stage} onValueChange={(stage) => (stage === SCHEDULED
          ? guard(lead, { proceed: () => update({ stage }), saved: load })
          : update({ stage }))}>
          <SelectTrigger className="h-11 w-full rounded-xl bg-white font-semibold sm:w-56" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
          <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s} data-testid={`stage-option-${s}`}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="mt-8 grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <FollowUpPanel data={data} leadId={id} onUpdate={update} reload={load} />
          <ConversationsPanel conversations={data.conversations} onChanged={load} />
        </div>
        <div className="space-y-6">
          <PersonalDataPanel lead={lead} onUpdate={update} />
          <CommercialPanel lead={lead} services={services} onUpdate={update} assignments={data.assignments} sellers={sellers} reload={load} />
          <AppointmentsPanel lead={lead} requests={data.appointment_requests} onChanged={load} />
          <ActivityPanel activity={data.activity} />
        </div>
      </div>
      {dialog}
    </div>
  );
}
